import { z } from "zod";
import { PortalProgrammeBodySchema } from "./programme-bodies.js";
/**
 * Engagement plan-view schemas — SSOT for stakeholders + milestones
 * shipped by Sprint B 7b'.
 *
 * The plan view is the centerpiece of the per-engagement portal
 * workspace: a curated register of who's involved (stakeholders,
 * spanning both sides) and where things stand (milestones with
 * status + next-action).
 *
 * Canon: docs/design-canon/04-shared/ssot.md (Zod first; routes
 * import these and use zValidator), docs/enhancements/portal-v1-2026-05.md.
 */
// ─── Engagement summary (read-side) ────────────────────────
export const EngagementSummarySchema = z.object({
    id: z.string().uuid(),
    portalOrgId: z.string().uuid(),
    name: z.string().min(1).max(200),
    description: z.string().nullable(),
    resourceKind: z.string().min(1).max(20),
    resourceId: z.string().uuid().nullable(),
    status: z.enum(["active", "completed", "paused"]),
    startedAt: z.string().nullable(),
    closedAt: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ─── Stakeholders ──────────────────────────────────────────
export const StakeholderSideSchema = z.enum(["l30", "customer", "external"]);
export const EngagementStakeholderSchema = z.object({
    id: z.string().uuid(),
    engagementId: z.string().uuid(),
    name: z.string().min(1).max(200),
    email: z.string().email().toLowerCase(),
    roleInEngagement: z.string().max(200).nullable(),
    side: StakeholderSideSchema,
    contactId: z.string().uuid().nullable(),
    notes: z.string().nullable(),
    archivedAt: z.string().datetime().nullable(),
    archivedBy: z.string().email().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const EngagementStakeholderCreateSchema = z.object({
    name: z.string().min(1).max(200),
    email: z.string().email().toLowerCase(),
    roleInEngagement: z.string().max(200).optional(),
    side: StakeholderSideSchema,
    contactId: z.string().uuid().optional(),
    notes: z.string().max(2000).optional(),
});
export const EngagementStakeholderUpdateSchema = z
    .object({
    name: z.string().min(1).max(200),
    email: z.string().email().toLowerCase(),
    roleInEngagement: z.string().max(200).nullable(),
    side: StakeholderSideSchema,
    contactId: z.string().uuid().nullable(),
    notes: z.string().max(2000).nullable(),
})
    .partial();
// ─── Milestones ────────────────────────────────────────────
export const MilestoneStatusSchema = z.enum([
    "upcoming",
    "on_track",
    "at_risk",
    "blocked",
    "achieved",
    "missed",
]);
export const EngagementMilestoneSchema = z.object({
    id: z.string().uuid(),
    engagementId: z.string().uuid(),
    title: z.string().min(1).max(300),
    description: z.string().nullable(),
    targetDate: z.string().nullable(),
    status: MilestoneStatusSchema,
    sortOrder: z.number().int(),
    nextAction: z.string().nullable(),
    nextActionOwnerEmail: z.string().email().nullable(),
    nextActionDueDate: z.string().nullable(),
    achievedAt: z.string().datetime().nullable(),
    archivedAt: z.string().datetime().nullable(),
    archivedBy: z.string().email().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const EngagementMilestoneCreateSchema = z.object({
    title: z.string().min(1).max(300),
    description: z.string().max(4000).optional(),
    targetDate: z.string().date().optional(),
    status: MilestoneStatusSchema.default("upcoming"),
    sortOrder: z.coerce.number().int().optional(),
    nextAction: z.string().max(2000).optional(),
    nextActionOwnerEmail: z.string().email().optional(),
    nextActionDueDate: z.string().date().optional(),
});
export const EngagementMilestoneUpdateSchema = z
    .object({
    title: z.string().min(1).max(300),
    description: z.string().max(4000).nullable(),
    targetDate: z.string().date().nullable(),
    status: MilestoneStatusSchema,
    sortOrder: z.coerce.number().int(),
    nextAction: z.string().max(2000).nullable(),
    nextActionOwnerEmail: z.string().email().nullable(),
    nextActionDueDate: z.string().date().nullable(),
})
    .partial();
// ─── List / read responses ─────────────────────────────────
/**
 * Single-shot read returned by the staff plan editor.
 * Includes the engagement record + its stakeholders + milestones.
 */
export const EngagementPlanResponseSchema = z.object({
    engagement: EngagementSummarySchema,
    stakeholders: z.array(EngagementStakeholderSchema),
    milestones: z.array(EngagementMilestoneSchema),
    // GoP programme collaboration plane (Task 11): calm, read-only list
    // of the engagement's active bodies + their chair, for the portal
    // plan view. Defaults to `[]` for tenants with no programme bodies
    // (and keeps older cached/mocked responses parseable).
    programmeBodies: z.array(PortalProgrammeBodySchema).default([]),
});
/** List of engagements for the staff selector. */
export const EngagementListResponseSchema = z.object({
    engagements: z.array(EngagementSummarySchema),
});
//# sourceMappingURL=engagement-plan.js.map